import { useCallback, useEffect, useState } from 'react';
import {
  NavLink,
  useParams,
  Outlet,
  generatePath,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import {
  IconArrowLeft,
  IconLoader,
  IconSelected,
  ajvResolver,
  Popup,
  ValidatorsProps,
  IconArrowRight,
  usePrevious,
  PermissionEnum,
  ResponseError,
  ButtonPrimary,
  responseErrorsResolver,
  formatBlacklistItem,
  BlacklistUserInfoData,
  BlacklistData,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  clearNewApplicationInfoState,
  checkChatMessages,
  saveChanges,
  setHintVisible,
  setMatchSelected,
  updateStepsStatus,
  onGroupSelect,
  setSelected,
  setAllowedDocumentsCategories,
} from './store/slices';
import StepItem from '../../../components/new-application/step-item';
import StepOne from '../../../components/new-application/individual/step-one';
import StepThree from '../../../components/new-application/individual/step-three';
import StepFour from '../../../components/new-application/individual/step-four';
import OngoingMonitoring from '../../../components/new-application/ongoing-monitoring';
import NewApplicationInfoTop from '../new-application-info-top';
import {
  useLazyGetNewApplicationsValidatorsQuery,
  useAddNewApplicationCommentMutation,
  useLazyGetNewApplicationInfoQuery,
  useLazyGetNewApplicationSystemLogsQuery,
  useUpdateCompanyOfficialApplicationMutation,
  useUpdateCompanyOfficialStatusMutation,
  useLazyGetCofNewApplicationDocumentsQuery,
  useLazyGetApplicationAgreementsQuery,
  useLazyGetApplicationChatQuery,
  useLazyGetApplicationOngoingMonitoringQuery,
  useApplyApplicationOngoingWarningActionMutation,
  useBatchApplyApplicationOngoingWarningActionMutation,
  useLazyGetApplicationOngoingStatusQuery,
  useRestartOngoingCheckingMutation,
  useLazyGetApplicationQuestionnarieQuery,
  useUpdateCOFQuestionnarieMutation,
  useResetApplicationWarningActionMutation,
  useLazyGetNewApplicationOrganizationQuery,
  useLazyGetApplicationWarningGroupWithPaginationQuery,
  useLazyGetApplicationOngoingWarningQuery,
  useLazyGetCustomerSourceOfWealthQuery,
  useLazyGetSourceOfIncomeDictionaryQuery,
  useLazyGetSourceOfWealthForIndividualDictionaryQuery,
  useLazyGetCOFCRSAndFactaQuery,
  useCreateCOFCRSAndFactaMutation,
  useUpdateCOFCRSAndFactaMutation,
  useDeleteCOFCRSAndFactaMutation,
  useLazyGetAffilatedCompaniesQuery,
  useCreateAffilatedCompaniesMutation,
  useDeleteAffilatedCompaniesMutation,
  useUpdateAffilatedCompaniesMutation,
} from '../store/services';
import {
  NewApplicationStepStatus,
  FormValues,
  OnSubmitType,
  ApplicationType,
  NewApplicationStatus,
  NewApplicationMainRepresentativeInfoTypes,
  AllowedCategory,
  CRSAndFatcaFormValues,
  AffilatedCompanyFormValues,
} from '../types';
import { updateApplicationStepStatuses } from './helpers/update-appliaction-step-status';
import {
  COFAndIndividualQuestionnarieFormValues,
  OngoingCheckingStatus,
  OngoingMonitring,
  OngoingStatus,
} from '../../customers/types';
import {
  useGetCustomersInfoDictionariesQuery,
  useLazyGetCustomersEmployeesListQuery,
} from '../../../store/dictionaries/services';
import getCofandIndividualQuestionnarieBody from '../individual/helpers/get-qestionnarie-body';

import ROUTES from '../../../routes';

import cn from '../new-application.module.css';

let retryTimeout: NodeJS.Timeout;

const NewApplicationMainRepresentativeInfo = ({
  isCompanyOfficial,
}: NewApplicationMainRepresentativeInfoTypes) => {
  const {
    newApplications: {
      individualInfo: {
        sourceOfWealth,
        sourceOfIncome,
        sourceOfWealthForIndividual,
      },
      mainRepresentative: {
        newApplicationInfo,
        hintVisible,
        systemLogs,
        stepsStatus: applicationStepsStatus,
        documents,
        selectedDocumentsCounts,
        agreements,
        ongoingMonitoring,
        ongoingCheckingStatus,
        questionnarie,
        changeLogs,
        changeLogsPagination,
        allowedDocumentsCategories,
        crsAndFatca,
        questionnarieId,
        affilatedCompanies,
      },
      list: { validators, applicationChat },
    },
    customersDictionaries: {
      countries,
      activePermissions,
      employeesList,
      currentEmployee,
      clientsActivities,
      baseCurrency,
      infoDictionariesIsLoading,
    },
  } = useAppSelector((state) => state);
  const { settings } = useAppSelector((state) => state.root);

  const uboPercentage = settings?.find(
    (item) => item.key === 'MINIMUM-UBO-OWNERSHIP-PERCENTAGE',
  )?.data?.value;

  const [defaultFormValues, setDefaultFormValues] = useState({});
  const [defaultQuestionnarieFormValues, setDefaultQuestionnarieFormValues] =
    useState({});
  const [statusChangeError, setStatusChangeError] = useState('');
  const [applyError, setApplyError] = useState('');
  const [applicationLoadErrorVisible, setApplicationLoadErrorVisible] =
    useState(false);
  const [selectedWarningId, setSelectedWarningId] = useState('');
  const [selectedWarningGroup, setSelectedWarningGroup] = useState('');
  const [blacklistData, setBlacklistData] = useState<BlacklistData | undefined>(
    undefined,
  );
  const [blacklistDataVisible, setBlacklistDataVisible] =
    useState<boolean>(false);
  const [blacklistItem, setBlacklistDataItem] =
    useState<BlacklistUserInfoData>();
  const [blacklistItemVisible, setBlacklistItemVisible] = useState(false);
  const [haveSecondPassport, setHaveSecondPassport] = useState(false);
  const [crsAndFatcaValues, setCrsAndFatcaValues] = useState<
    CRSAndFatcaFormValues[]
  >([
    {
      id: 'crs-and-fatca-1',
      residenceCountryId: '',
      taxIdentificationNumber: '',
      noTinReason: '',
    },
  ]);
  const [selectedCrsAndFatcaId, setSelectedCrsAndFatcaId] = useState('');
  const [affilatedCompaniesValues, setAffilatedCompaniesValues] = useState<
    AffilatedCompanyFormValues[]
  >([
    {
      id: 'affilated-company-1',
      name: '',
      registrationNumber: '',
      mainActivities: '',
      position: '',
      countryId: '',
      percentageOfOwnership: '',
    },
  ]);
  const [selectedAffilatedCompanyId, setSelectedAffilatedCompanyId] =
    useState('');

  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
    setError,
    clearErrors,
    setValue,
  } = useForm<FormValues>({
    resolver: ajvResolver(
      validators?.updateClientApplicationSchema as ValidatorsProps,
    ),
  });

  const {
    control: questionnarieControl,
    reset: questionnarieReset,
    clearErrors: questionnarieClearErrors,
    formState: { errors: questionnarieErrors, isDirty: questionnarieIsDirty },
    watch: questionnarieWatch,
    getValues: questionnarieGetValues,
    setError: questionnarieSetError,
    setValue: questionnarieSetValue,
  } = useForm<COFAndIndividualQuestionnarieFormValues>();

  const [popupVisible, setPopupVisible] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [userCreated, setUserCreated] = useState(false);

  useGetCustomersInfoDictionariesQuery();

  const { search, state } = useLocation();

  const fields = watch();
  const questionnarieFields = questionnarieWatch();

  const prevClientActivitiy = usePrevious(fields.clientActivityId);

  const employmentActivity = clientsActivities?.find(
    (item) => item.name === 'Employment',
  );
  const otherActivity = clientsActivities?.find(
    (item) => item.name === 'Other',
  );
  const businessOwnerActivity = clientsActivities?.find(
    (item) => item.name === 'Business Owner',
  );

  const dispatch = useAppDispatch();
  const { applicationId } = useParams();

  const stepsStatus = updateApplicationStepStatuses({
    values: fields,
    stepsStatus: applicationStepsStatus,
    agreements,
    ongoingMonitoring,
    ongoingCheckingStatus,
    applicationScheme: newApplicationInfo?.applicationScheme,
    questionnarieFields,
    crsAndFatcaValues,
    haveSecondPassport,
    documents,
    affilatedCompaniesValues,
    clientsActivities,
  });

  const prevStepsStatus = usePrevious(stepsStatus);
  const prevApplicationId = usePrevious(applicationId);

  const [getApplicationChat] = useLazyGetApplicationChatQuery();
  const [getInfo, { isLoading, isFetching }] =
    useLazyGetNewApplicationInfoQuery({
      refetchOnReconnect: true,
      refetchOnFocus: true,
    });
  const [getApplicationOrganization] =
    useLazyGetNewApplicationOrganizationQuery();
  const [updateInfo, { isLoading: requestInProgress }] =
    useUpdateCompanyOfficialApplicationMutation();
  const [getValidators] = useLazyGetNewApplicationsValidatorsQuery();
  const [createCompanyOfficial, { isLoading: createinProgress }] =
    useUpdateCompanyOfficialStatusMutation();
  const [
    addResponsibleToApplication,
    {
      isLoading: addResponsibleInProgress,
      isSuccess: addResponsibleIsSuccess,
      isError: addResponsibleIsError,
    },
  ] = useUpdateCompanyOfficialStatusMutation();
  const [
    rejectCompanyOfficialApplication,
    {
      isLoading: rejectInProgress,
      isSuccess: rejectIsSuccess,
      isError: rejectIsError,
    },
  ] = useUpdateCompanyOfficialStatusMutation();
  const [backToIncomplete, { isLoading: backToIncompleteInProgress }] =
    useUpdateCompanyOfficialStatusMutation();
  const [getSystemLogs] = useLazyGetNewApplicationSystemLogsQuery();
  const [
    addComment,
    { isLoading: sendCommentInProgress, isSuccess: sendCommentIsSuccess },
  ] = useAddNewApplicationCommentMutation();
  const [getCofApplicationDocuments] =
    useLazyGetCofNewApplicationDocumentsQuery();
  const [getApplicationAgreements] = useLazyGetApplicationAgreementsQuery();
  const [getCustomersEmployeesList] = useLazyGetCustomersEmployeesListQuery();
  const [
    getApplicationOngoingMonitoring,
    { isLoading: ongoingIsLoading, isFetching: ongoingIsFetching },
  ] = useLazyGetApplicationOngoingMonitoringQuery();
  const [
    applyApplicationOngoingWarning,
    { isLoading: applyWarningInProgress },
  ] = useApplyApplicationOngoingWarningActionMutation();
  const [
    batchApplyApplicationOngoingWarning,
    {
      isLoading: batchApplyWarningInProgress,
      isSuccess: batchApplyWarningIsSuccess,
    },
  ] = useBatchApplyApplicationOngoingWarningActionMutation();
  const [getOngoingMonitoringStatus, { isLoading: ongoingStatusIsLoading }] =
    useLazyGetApplicationOngoingStatusQuery();
  const [restartOngoingChecking, { isLoading: restartInProgress }] =
    useRestartOngoingCheckingMutation();
  const [
    resetApplicationWarningsWarning,
    {
      isLoading: resetTransactionsWarningIsLoading,
      isSuccess: resetTransactionsWarningIsSuccess,
    },
  ] = useResetApplicationWarningActionMutation();

  const [
    getApplicationQuestionnarie,
    { isLoading: questionnarieIsLoading, isFetching: questionnarieIsFetching },
  ] = useLazyGetApplicationQuestionnarieQuery();
  const [updateCOFQuestionnarie, { isLoading: questionnarieUpdateInProgress }] =
    useUpdateCOFQuestionnarieMutation();
  const [
    getApplicationWarning,
    {
      isLoading: paginationInProgress,
      isFetching: paginationIsFetching,
      isSuccess: paginationIsSuccess,
    },
  ] = useLazyGetApplicationWarningGroupWithPaginationQuery();
  const [
    getApplicationOngoingWarningById,
    { isLoading: isWarningByIdLoading, isFetching: isWarningByIdFetching },
  ] = useLazyGetApplicationOngoingWarningQuery();
  const [getCustomerSourceOfWealth] = useLazyGetCustomerSourceOfWealthQuery();
  const [getSourceOfIncomeDictionary] =
    useLazyGetSourceOfIncomeDictionaryQuery();
  const [getSourceOfWealthForIndividualDictionary] =
    useLazyGetSourceOfWealthForIndividualDictionaryQuery();
  const [
    getCrsAndFatca,
    { isLoading: crsAndFatcaIsLoading, isFetching: crsAndFatcaIsFetching },
  ] = useLazyGetCOFCRSAndFactaQuery();
  const [createCrsAndFatca, { isLoading: createCrsAndFatcaIsProgress }] =
    useCreateCOFCRSAndFactaMutation();
  const [updateCrsAndFatca, { isLoading: updateCrsAndFatcaInProgress }] =
    useUpdateCOFCRSAndFactaMutation();
  const [
    deleteCrsAndFatca,
    {
      isLoading: deleteCrsAndFatcaInProgress,
      isSuccess: deleteCrsAndFatcaIsSuccess,
    },
  ] = useDeleteCOFCRSAndFactaMutation();
  const [
    getAffilatedCompanies,
    {
      isLoading: affilatedCompaniesIsLoading,
      isFetching: affilatedCompaniesIsFetching,
    },
  ] = useLazyGetAffilatedCompaniesQuery();
  const [
    createAffilatedCompany,
    { isLoading: createAffilatedCompanyInProgress },
  ] = useCreateAffilatedCompaniesMutation();
  const [
    updateAffilatedCompany,
    { isLoading: updateAffilatedCompanyInProgress },
  ] = useUpdateAffilatedCompaniesMutation();
  const [
    deleteAffilatedCompany,
    {
      isLoading: deleteAffilatedCompanyInProgress,
      isSuccess: deleteAffilatedCompanyIsSuccess,
    },
  ] = useDeleteAffilatedCompaniesMutation();

  const onSaveCRSAndFatca = async () => {
    const crsAndFatcaForCreation: CRSAndFatcaFormValues[] = [];
    const crsAndFatcaForForUpdate: CRSAndFatcaFormValues[] = [];

    crsAndFatcaValues?.forEach((item) => {
      if (item.id?.includes('crs-and-fatca')) {
        crsAndFatcaForCreation.push(item);
      } else {
        const currentcrsAndFatca = crsAndFatca?.find(
          (value) => value.id === item.id,
        );

        if (currentcrsAndFatca) {
          if (
            !isEqual(item, {
              id: currentcrsAndFatca.id,
              residenceCountryId: currentcrsAndFatca?.residenceCountryId,
              taxIdentificationNumber:
                currentcrsAndFatca?.taxIdentificationNumber,
              noTinReason: currentcrsAndFatca?.noTinReason,
            })
          ) {
            crsAndFatcaForForUpdate.push(item);
          }
        }
      }
    });

    if (crsAndFatcaForCreation?.length) {
      await Promise.all(
        crsAndFatcaForCreation
          ?.filter((item) => item.residenceCountryId)
          ?.map(async (item) => {
            const response = await createCrsAndFatca({
              id: questionnarieId as string,
              body: {
                residenceCountryId: item.residenceCountryId,
                taxIdentificationNumber: item.taxIdentificationNumber,
                noTinReason: item.noTinReason,
              },
            });

            return response;
          }),
      );
    }

    if (crsAndFatcaForForUpdate?.length) {
      await Promise.all(
        crsAndFatcaForForUpdate?.map(async (item) => {
          const response = await updateCrsAndFatca({
            id: item.id as string,
            body: {
              residenceCountryId: item.residenceCountryId,
              taxIdentificationNumber: item.taxIdentificationNumber,
              noTinReason: item.noTinReason,
            },
          });

          return response;
        }),
      );
    }
  };

  const onDeleteCRSAndFatca = async (id: string) => {
    if (id.includes('crs-and-fatca')) {
      setCrsAndFatcaValues(crsAndFatcaValues?.filter((item) => item.id !== id));
    } else {
      setSelectedCrsAndFatcaId(id);

      await deleteCrsAndFatca(id);
    }
  };

  const onSaveAffilatedCompanies = async () => {
    const affilatedCompaniesForCreate: AffilatedCompanyFormValues[] = [];
    const affilatedCompaniesForForUpdate: AffilatedCompanyFormValues[] = [];

    affilatedCompaniesValues?.forEach((item) => {
      if (item.id?.includes('affilated-company')) {
        affilatedCompaniesForCreate.push(item);
      } else {
        const currentAffilatedCompany = affilatedCompanies?.find(
          (value) => value.id === item.id,
        );

        if (currentAffilatedCompany) {
          if (
            !isEqual(item, {
              id: currentAffilatedCompany.id,
              name: currentAffilatedCompany.name,
              registrationNumber: currentAffilatedCompany.registrationNumber,
              mainActivities: currentAffilatedCompany.mainActivities,
              position: currentAffilatedCompany.position,
              countryId: currentAffilatedCompany.countryId,
              percentageOfOwnership:
                currentAffilatedCompany.percentageOfOwnership,
            })
          ) {
            affilatedCompaniesForForUpdate.push(item);
          }
        }
      }
    });

    if (affilatedCompaniesForCreate?.length) {
      await Promise.all(
        affilatedCompaniesForCreate
          ?.filter(
            (item) =>
              item.name &&
              item.registrationNumber &&
              item.mainActivities &&
              item.position &&
              item.countryId &&
              item.percentageOfOwnership,
          )
          ?.map(async (item) => {
            const response = await createAffilatedCompany({
              id: questionnarieId as string,
              body: {
                name: item.name,
                registrationNumber: item.registrationNumber,
                mainActivities: item.mainActivities,
                position: item.position,
                countryId: item.countryId,
                percentageOfOwnership: Number(item.percentageOfOwnership),
              },
            });

            return response;
          }),
      );
    }

    if (affilatedCompaniesForForUpdate?.length) {
      await Promise.all(
        affilatedCompaniesForForUpdate?.map(async (item) => {
          const response = await updateAffilatedCompany({
            id: item.id as string,
            body: {
              name: item.name,
              registrationNumber: item.registrationNumber,
              mainActivities: item.mainActivities,
              position: item.position,
              countryId: item.countryId,
              percentageOfOwnership: Number(item.percentageOfOwnership),
            },
          });

          return response;
        }),
      );
    }
  };

  const onDeleteAffilatedCompany = async (id: string) => {
    if (id.includes('affilated-company')) {
      setAffilatedCompaniesValues(
        affilatedCompaniesValues?.filter((item) => item.id !== id),
      );
    } else {
      setSelectedAffilatedCompanyId(id);

      await deleteAffilatedCompany(id);
    }
  };

  const getApplicationOngoingWarningByIdAction = async ({
    id,
    warningId,
  }: {
    id: string;
    warningId: string;
  }) => {
    const response = await getApplicationOngoingWarningById({
      appId: applicationId as string,
      warningId,
      id,
    });

    if (
      !blacklistData &&
      !blacklistItem &&
      !blacklistDataVisible &&
      !blacklistItemVisible &&
      response
    ) {
      if (response?.data?.data?.blacklistDoc) {
        setBlacklistData(response?.data?.data?.blacklistDoc);
        setBlacklistDataVisible(true);
      } else if (response?.data?.data?.blacklistItem) {
        setBlacklistDataItem(
          formatBlacklistItem(response?.data?.data?.blacklistItem),
        );
        setBlacklistItemVisible(true);
      }
    }
  };

  const getApplicationWarningsAction = async ({
    warningGroupId,
    cursor,
  }: {
    warningGroupId: string;
    cursor: string;
  }) => {
    setSelectedWarningGroup(warningGroupId);

    await getApplicationWarning({
      id: applicationId as string,
      warningGroupId,
      cursor,
    });
  };

  const batchApplyApplicationOngoingWarningAction = async ({
    action,
    comment,
    matchStatus,
  }: {
    action: string;
    comment: string;
    matchStatus?: string;
  }) => {
    setApplyError('');

    const ongoingGroupsWithoutWanings: OngoingMonitring[] = [];

    ongoingMonitoring?.forEach((item) => {
      item?.warnings?.edges?.forEach((warning) => {
        if (!warning.node.action) {
          ongoingGroupsWithoutWanings.push(item);
        }
      });
    });

    if (ongoingGroupsWithoutWanings && ongoingGroupsWithoutWanings.length) {
      await Promise.all(
        ongoingGroupsWithoutWanings?.map(async (item) => {
          const response = (await batchApplyApplicationOngoingWarning({
            appId: applicationId as string,
            body: {
              warningGroupId: item.id,
              action,
              employeeId: currentEmployee?.id as string,
              comment,
              matchStatus,
            },
          })) as { error: ResponseError };

          if (response.error) {
            if (response.error.data.message) {
              if (
                Array.isArray(response.error.data.message) &&
                response.error.data.message[0].property === 'coreError'
              ) {
                setApplyError(response.error.data.message[0].value as string);
              } else if (typeof response.error.data.message === 'string') {
                setApplyError(response.error.data.message);
              } else {
                setApplyError('Internal Server Error. Try Again');
              }
            }
          }

          return response;
        }),
      );
    }
  };

  const onResetTransactionWarningAction = async (
    warningId: string,
    id: string,
  ) => {
    setSelectedWarningId(warningId);

    const response = (await resetApplicationWarningsWarning({
      appId: applicationId as string,
      id,
      warningId,
    })) as { error: ResponseError };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setApplyError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setApplyError(response.error.data.message);
        } else {
          setApplyError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const applyApplicationOngoingWarningAction = async ({
    id,
    warningId,
    action,
    comment,
    matchStatus,
  }: {
    id: string;
    warningId: string;
    action: string;
    comment: string;
    matchStatus?: string;
  }) => {
    setApplyError('');

    const response = (await applyApplicationOngoingWarning({
      appId: applicationId as string,
      id,
      warningId,
      body: {
        action,
        comment,
        employeeId: currentEmployee?.id as string,
        matchStatus,
      },
    })) as { error: ResponseError };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setApplyError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setApplyError(response.error.data.message);
        } else {
          setApplyError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const addCommentAction = useCallback(
    (text: string) => {
      addComment({
        id: applicationId as string,
        body: {
          text,
        },
      });
    },
    [applicationId],
  );

  const onCreateCustomer = async () => {
    setStatusChangeError('');

    const response = (await createCompanyOfficial({
      id: applicationId as string,
      body: { status: NewApplicationStatus.ACCEPTED },
    })) as { error?: { data?: { message?: string } } };

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    } else {
      setUserCreated(true);

      setTimeout(() => {
        getApplicationOrganization(applicationId as string);
      }, 8000);
    }
  };

  const onRejectAplication = async () => {
    setStatusChangeError('');

    const response = (await rejectCompanyOfficialApplication({
      id: applicationId as string,
      body: { status: NewApplicationStatus.DECLINED },
    })) as { error?: { data?: { message?: string } } };

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    }
  };

  const onBackToIncomplete = async () => {
    setStatusChangeError('');

    const response = (await backToIncomplete({
      id: applicationId as string,
      body: { status: NewApplicationStatus.INCOMPLETE },
    })) as { error?: { data?: { message?: string } } };

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    }
  };

  const onAddResponsible = async (data: {
    status: NewApplicationStatus;
    id?: string | undefined;
  }) => {
    setStatusChangeError('');

    const response = addResponsibleToApplication({
      id: applicationId as string,
      body: {
        status: data.status,
        reviewEmployeeId: data.id,
      },
    }) as { error?: { data?: { message?: string } } };

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    }
  };

  const getSystemLogsAction = useCallback(() => {
    if (applicationId) {
      getSystemLogs(applicationId);
    }
  }, [applicationId]);

  const getInfoAction = useCallback(async () => {
    if (applicationId) {
      const response = (await getInfo(applicationId)) as {
        error: ResponseError;
        data: any;
      };

      getApplicationChat(applicationId);

      if (activePermissions?.includes(PermissionEnum.USERS_LIST)) {
        getCustomersEmployeesList();
      }

      if (response) {
        if (response?.error?.data?.message) {
          if (
            Array.isArray(response.error.data.message) &&
            response.error.data.message[0].property === 'coreError'
          ) {
            setApplicationLoadErrorVisible(true);
          }
        }
      }
    }
  }, [applicationId, activePermissions]);

  const onSubmit = handleSubmit(async (values) => {
    setApplyError('');
    dispatch(saveChanges(values));

    const body: {
      firstName?: string;
      lastName?: string;
      nationalityId?: string;
      email?: string;
      dob?: string;
      deliveryChannelId?: string;
      taxNumber?: string;
      clientActivityId?: string;
      nameOfEmployer?: string;
      employeePosition?: string;
      countryOfEmployerId?: string;
      typeOfActivityDescription?: string;
    } = {};

    if (
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
      ) &&
      newApplicationInfo?.basePersonalInfoEditable
    ) {
      if (values.firstName) {
        body.firstName = values.firstName;
      }

      if (values.lastName) {
        body.lastName = values.lastName;
      }

      if (values.email) {
        body.email = values.email;
      }

      if (values.dob) {
        const dateValue = new Date(values.dob);

        const dob = `${dateValue.getFullYear()}-${(dateValue.getMonth() + 1)
          .toString()
          .padStart(2, '0')}-${dateValue
          .getDate()
          .toString()
          .padStart(2, '0')}`;

        body.dob = dob;
      }
    }

    if (values.nationalityId) {
      body.nationalityId = values.nationalityId;
    }

    if (values.taxNumber) {
      body.taxNumber = values.taxNumber;
    }

    if (values.deliveryChannelId) {
      body.deliveryChannelId = values.deliveryChannelId;
    }

    if (values.clientActivityId) {
      body.clientActivityId = values.clientActivityId;
    }

    if (values.nameOfEmployer) {
      body.nameOfEmployer = values.nameOfEmployer;
    }

    if (values.employeePosition) {
      body.employeePosition = values.employeePosition;
    }

    if (values.countryOfEmployerId) {
      body.countryOfEmployerId = values.countryOfEmployerId;
    }

    if (values.typeOfActivityDescription) {
      body.typeOfActivityDescription = values.typeOfActivityDescription;
    }

    const response = (await updateInfo({
      body: {
        ...body,
        participatesInPolitics: Boolean(
          values.participatesInPolitics === 'yes',
        ),
        isUSResident: Boolean(values.isUSResident === 'yes'),
      },
      id: applicationId as string,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError,
        setGlobalError: setApplyError,
      });
    }

    if (
      (newApplicationInfo?.applicationScheme &&
        (newApplicationInfo?.applicationScheme?.civilActive ||
          newApplicationInfo?.applicationScheme?.criminalOfAnyActive ||
          newApplicationInfo?.applicationScheme
            ?.lastThreeYearsAddressHistoryActive ||
          newApplicationInfo?.applicationScheme
            ?.nationalInsuranceNumberOfDirectorsActive ||
          newApplicationInfo?.applicationScheme?.regulatoryActive ||
          newApplicationInfo?.applicationScheme
            ?.businessAndEmploymentActive)) ||
      newApplicationInfo?.applicationScheme?.id ===
        'a87db4b4-3c78-4564-bba1-c16a19553159'
    ) {
      const questionnarieValues = questionnarieGetValues();

      const questionnarieBody = getCofandIndividualQuestionnarieBody({
        questionnarieValues,
        scheme: newApplicationInfo?.applicationScheme,
      });

      const questionnarieResponse = (await updateCOFQuestionnarie({
        id: newApplicationInfo?.id,
        body: questionnarieBody,
      })) as { error?: ResponseError };

      if (questionnarieResponse?.error) {
        responseErrorsResolver({
          errors: questionnarieResponse?.error,
          setError: questionnarieSetError,
          setGlobalError: setApplyError,
        });
      }

      if (
        newApplicationInfo?.applicationScheme?.id ===
        'a87db4b4-3c78-4564-bba1-c16a19553159'
      ) {
        onSaveCRSAndFatca();
        onSaveAffilatedCompanies();
      }
    }
  });

  const retryOngoinStatusAction = async () => {
    clearTimeout(retryTimeout);

    const response = (await getOngoingMonitoringStatus(
      newApplicationInfo?.id as string,
    )) as { data?: { data?: { edges: { node: OngoingCheckingStatus }[] } } };

    if (response?.data?.data) {
      if (
        response?.data?.data?.edges?.find(
          (item) => item.node.status === OngoingStatus.SUCCESS,
        ) ||
        response?.data?.data?.edges?.find(
          (item) => item.node.status === OngoingStatus.FAIL,
        )
      ) {
        clearTimeout(retryTimeout);
      } else {
        retryTimeout = setTimeout(() => {
          retryOngoinStatusAction();
        }, 60000);
      }
    }
  };

  const restartOngoingCheckingAction = async () => {
    const response = (await restartOngoingChecking({
      applicationId: newApplicationInfo?.id as string,
    })) as { data?: { data?: OngoingCheckingStatus } };

    if (response?.data) {
      retryOngoinStatusAction();
    }
  };

  useEffect(() => {
    if ((!newApplicationInfo && applicationId) || !prevApplicationId) {
      getInfoAction();
      getSystemLogsAction();
    }
  }, [applicationId, getInfoAction, newApplicationInfo, prevApplicationId]);

  useEffect(() => {
    if (prevStepsStatus && !isEqual(stepsStatus, prevStepsStatus)) {
      dispatch(updateStepsStatus(stepsStatus));
    }
  }, [stepsStatus, prevStepsStatus, dispatch]);

  useEffect(() => {
    if (
      applicationId &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_LIST,
      )
    ) {
      getCofApplicationDocuments(applicationId);
    }
  }, [applicationId]);

  useEffect(() => {
    if (
      newApplicationInfo &&
      newApplicationInfo.status !== NewApplicationStatus.INCOMPLETE &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_AML_LIST,
      )
    ) {
      retryOngoinStatusAction();
    }
  }, [newApplicationInfo]);

  useEffect(() => {
    if (crsAndFatca) {
      setCrsAndFatcaValues(
        crsAndFatca?.map((item) => ({
          id: item.id,
          residenceCountryId: item?.residenceCountryId,
          taxIdentificationNumber: item?.taxIdentificationNumber || '',
          noTinReason: item?.noTinReason || '',
        })),
      );
    }
  }, [crsAndFatca]);

  useEffect(() => {
    if (affilatedCompanies) {
      setAffilatedCompaniesValues(
        affilatedCompanies?.map((item) => ({
          id: item.id,
          name: item.name,
          registrationNumber: item.registrationNumber,
          mainActivities: item.mainActivities,
          position: item.position,
          countryId: item.countryId,
          percentageOfOwnership: item.percentageOfOwnership?.toString(),
        })),
      );
    }
  }, [affilatedCompanies]);

  useEffect(() => {
    if (deleteCrsAndFatcaIsSuccess) {
      setSelectedCrsAndFatcaId('');
    }
  }, [deleteCrsAndFatcaIsSuccess]);

  useEffect(() => {
    if (deleteAffilatedCompanyIsSuccess) {
      setSelectedAffilatedCompanyId('');
    }
  }, [deleteAffilatedCompanyIsSuccess]);

  useEffect(() => {
    if (
      ongoingCheckingStatus &&
      ongoingCheckingStatus?.status === OngoingStatus.SUCCESS &&
      newApplicationInfo &&
      newApplicationInfo.status !== NewApplicationStatus.INCOMPLETE &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_AML_LIST,
      )
    ) {
      getApplicationOngoingMonitoring(newApplicationInfo.id);
    }
  }, [ongoingCheckingStatus, newApplicationInfo]);

  useEffect(() => {
    if (!validators) {
      getValidators();
    }
  }, [getValidators, validators]);

  useEffect(() => {
    if (rejectIsSuccess || rejectIsError) {
      setPopupVisible(false);
    }
  }, [rejectIsSuccess, rejectIsError]);

  useEffect(() => {
    if (paginationIsSuccess) {
      setSelectedWarningGroup('');
    }
  }, [paginationIsSuccess]);

  useEffect(() => {
    if (newApplicationInfo) {
      if (newApplicationInfo?.applicationScheme) {
        if (
          newApplicationInfo?.applicationScheme?.civilActive ||
          newApplicationInfo?.applicationScheme?.criminalOfAnyActive ||
          newApplicationInfo?.applicationScheme
            ?.lastThreeYearsAddressHistoryActive ||
          newApplicationInfo?.applicationScheme
            ?.nationalInsuranceNumberOfDirectorsActive ||
          newApplicationInfo?.applicationScheme?.regulatoryActive ||
          newApplicationInfo?.applicationScheme?.businessAndEmploymentActive ||
          newApplicationInfo?.hasQuestionnaire
        ) {
          if (!questionnarie) {
            getApplicationQuestionnarie(newApplicationInfo?.id);
          }
        }
      }
    }
  }, [newApplicationInfo]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.DICTIONARIES_ITEMS_LIST) &&
      newApplicationInfo &&
      Boolean(
        newApplicationInfo?.applicationScheme?.id ===
        'a87db4b4-3c78-4564-bba1-c16a19553159',
      )
    ) {
      if (!sourceOfWealth) {
        getCustomerSourceOfWealth();
      }

      if (!sourceOfIncome) {
        getSourceOfIncomeDictionary();
      }

      if (!sourceOfWealthForIndividual) {
        getSourceOfWealthForIndividualDictionary();
      }
    }
  }, [
    sourceOfWealth,
    activePermissions,
    newApplicationInfo,
    sourceOfIncome,
    sourceOfWealthForIndividual,
  ]);

  useEffect(() => {
    if (
      newApplicationInfo &&
      Boolean(
        newApplicationInfo?.applicationScheme?.id ===
        'ad811a02-87ed-4e19-ad45-770c0fd8fe41',
      )
    ) {
      const nextAllowedCategories =
        newApplicationInfo?.applicationScheme?.documentsCategories?.edges?.map(
          (item) => item.node,
        ) as AllowedCategory[];

      nextAllowedCategories?.unshift({
        id: 'identification-category',
        name: 'Identification documents',
        required: false,
      });

      dispatch(setAllowedDocumentsCategories(nextAllowedCategories));
    }
  }, [newApplicationInfo]);

  useEffect(() => {
    if (
      questionnarieId &&
      newApplicationInfo &&
      Boolean(
        newApplicationInfo?.applicationScheme?.id ===
        'a87db4b4-3c78-4564-bba1-c16a19553159',
      )
    ) {
      const nextAllowedCategories =
        newApplicationInfo?.applicationScheme?.documentsCategories?.edges?.map(
          (item) => item.node,
        ) as AllowedCategory[];

      nextAllowedCategories?.unshift({
        id: 'identification-category',
        name: 'Identification documents',
        required: false,
      });

      dispatch(setAllowedDocumentsCategories(nextAllowedCategories));

      if (!crsAndFatca) {
        getCrsAndFatca(questionnarieId);
      }

      if (!affilatedCompanies) {
        getAffilatedCompanies(questionnarieId);
      }
    }
  }, [questionnarieId, newApplicationInfo, crsAndFatca, affilatedCompanies]);

  useEffect(() => {
    if (
      prevClientActivitiy &&
      prevClientActivitiy === employmentActivity?.value &&
      fields.clientActivityId !== employmentActivity?.value
    ) {
      setValue('nameOfEmployer', '');
      setValue('countryOfEmployerId', '');
      setValue('employeePosition', '');
      questionnarieSetValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy === businessOwnerActivity?.value &&
      fields.clientActivityId !== businessOwnerActivity?.value
    ) {
      setValue('countryOfEmployerId', '');
      questionnarieSetValue('nameOfCompany', '');
      questionnarieSetValue('ownership', '');
      questionnarieSetValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy ===
        clientsActivities?.find((item) => item.name === 'Self-employed')
          ?.value &&
      fields.clientActivityId !==
        clientsActivities?.find((item) => item.name === 'Self-employed')?.value
    ) {
      questionnarieSetValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy === otherActivity?.value &&
      fields.clientActivityId !== otherActivity?.value
    ) {
      setValue('typeOfActivityDescription', '');
    }
  }, [fields.clientActivityId, prevClientActivitiy]);

  useEffect(() => {
    if (questionnarie) {
      questionnarieReset(questionnarie);
      setDefaultQuestionnarieFormValues(questionnarie);
    }
  }, [questionnarie]);

  useEffect(() => {
    if (newApplicationInfo) {
      const formValues = {
        firstName: newApplicationInfo.firstName || '',
        lastName: newApplicationInfo.lastName || '',
        phone: newApplicationInfo.phone || '',
        email: newApplicationInfo.email || '',
        dob: newApplicationInfo.dob || '',
        city: newApplicationInfo?.city || '',
        countryId: newApplicationInfo?.country?.id || '',
        postalCode: newApplicationInfo?.postalCode || '',
        address1: newApplicationInfo?.address1 || '',
        address2: newApplicationInfo?.address2 || '',
        passportNumber: newApplicationInfo.passportNumber || '',
        passportExpiredDate: newApplicationInfo.passportExpiredDate || '',
        participatesInPolitics: !newApplicationInfo.participatesInPolitics
          ? 'no'
          : 'yes',
        isUSResident: !newApplicationInfo.isUSResident ? 'no' : 'yes',
        nationalityId: newApplicationInfo.nationalityId || '',
        deliveryChannelId: newApplicationInfo.deliveryChannelId || '',
        userId: newApplicationInfo.userId || '',
        sumSubStatus: newApplicationInfo?.sumSubApplication?.reviewAnswer || '',
        taxNumber: newApplicationInfo?.taxNumber || '',
        birthCountryId: newApplicationInfo?.birthCountryId || '',
        placeOfBirth: newApplicationInfo?.placeOfBirth || '',
        correspondenceAddressSameAsRegisteredAddress:
          newApplicationInfo?.correspondenceAddressSameAsRegisteredAddress,
        correspondenceCountryId:
          newApplicationInfo?.correspondenceCountryId || '',
        correspondenceCity: newApplicationInfo?.correspondenceCity || '',
        correspondencePostalCode:
          newApplicationInfo?.correspondencePostalCode || '',
        correspondenceAddress1:
          newApplicationInfo?.correspondenceAddress1 || '',
        correspondenceAddress2:
          newApplicationInfo?.correspondenceAddress2 || '',
        clientActivityId: newApplicationInfo?.clientActivityId || '',
        nameOfEmployer: newApplicationInfo?.nameOfEmployer || '',
        employeePosition: newApplicationInfo?.employeePosition || '',
        countryOfEmployerId: newApplicationInfo?.countryOfEmployerId || '',
        typeOfActivityDescription:
          newApplicationInfo?.typeOfActivityDescription || '',
      };

      reset(formValues);
      setDefaultFormValues(formValues);
      getApplicationAgreements(newApplicationInfo.id);
    }
  }, [reset, dispatch, newApplicationInfo]);

  useEffect(() => {
    setTimeout(() => {
      dispatch(checkChatMessages());
    }, 60000);
  }, [dispatch]);

  useEffect(() => {
    if ((addResponsibleIsSuccess || addResponsibleIsError) && applicationId) {
      getCofApplicationDocuments(applicationId);
    }
  }, [addResponsibleIsSuccess, applicationId, addResponsibleIsError]);

  useEffect(() => {
    const handleBeforeUnload = (event: {
      preventDefault: () => void;
      returnValue: string;
    }) => {
      event.preventDefault();

      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };

    if (
      !isEqual(fields, defaultFormValues) ||
      !isEqual(questionnarieFields, defaultQuestionnarieFormValues)
    ) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [
    fields,
    defaultFormValues,
    questionnarieFields,
    defaultQuestionnarieFormValues,
  ]);

  useEffect(() => {
    let title = 'New Application';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;

  if (
    !userCreated &&
    (isLoading ||
      infoDictionariesIsLoading ||
      isFetching ||
      questionnarieIsFetching ||
      questionnarieIsLoading)
  ) {
    content = (
      <div className={cn.newApplicationInfoContentLoader}>
        <IconLoader />
      </div>
    );
  } else if (applicationLoadErrorVisible) {
    content = (
      <div className={cn.applicationLoadError}>
        <div className={cn.applicationLoadErrorTitle}>
          Something went wrong. Reload page
        </div>
        <ButtonPrimary
          text="Reload"
          className={cn.reloadButton}
          onClick={() => window.location.reload()}
          bold
        />
      </div>
    );
  } else {
    content = (
      <div className={cn.newApplicationInfoContent}>
        <NewApplicationInfoTop
          status={newApplicationInfo?.status}
          reviewStatus={newApplicationInfo?.reviewStatus}
          setPopupVisible={(data) => setPopupVisible(data)}
          stepsStatus={applicationStepsStatus}
          setMatchSelected={(data) => dispatch(setMatchSelected(data))}
          onSendComment={(text) => addCommentAction(text)}
          applicationType={ApplicationType.COMPANY_OFFICIAL}
          systemLogs={systemLogs}
          matches={newApplicationInfo?.similarApplications?.edges}
          reviewedDuplicates={newApplicationInfo?.reviewedDuplicates}
          reviewDuplicatesComment={newApplicationInfo?.reviewDuplicatesComment}
          sendCommentInProgress={sendCommentInProgress}
          sendCommentIsSuccess={sendCommentIsSuccess}
          applicationId={applicationId}
          comments={newApplicationInfo?.comments?.edges.map(
            (item) => item?.node,
          )}
          onSubmit={onSubmit as OnSubmitType}
          requestInProgress={
            requestInProgress ||
            questionnarieUpdateInProgress ||
            createCrsAndFatcaIsProgress ||
            updateCrsAndFatcaInProgress ||
            createAffilatedCompanyInProgress ||
            updateAffilatedCompanyInProgress
          }
          fields={fields}
          questionnarieFields={questionnarieFields}
          defaultFormValues={defaultFormValues}
          defaultQuestionnarieFormValues={defaultQuestionnarieFormValues}
          isCompanyOfficial={isCompanyOfficial}
          createdDate={newApplicationInfo?.createdAt as string}
          statusChangeError={statusChangeError}
          isEditable={activePermissions?.includes(
            PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
          )}
          createAvailable={activePermissions?.includes(
            PermissionEnum.APPLICATIONS_CUSTOMERS_CREATE_ACTION,
          )}
          onCreate={() => onCreateCustomer()}
          onBackToIncomplete={() => onBackToIncomplete()}
          backToIncompleteInProgress={backToIncompleteInProgress}
          reviewEmployee={newApplicationInfo?.reviewEmployee}
          onAddResponsible={(data) => onAddResponsible(data)}
          createInProgress={createinProgress}
          canSelectResponsible={activePermissions?.includes(
            PermissionEnum.USERS_LIST,
          )}
          addResponsibleInProgress={addResponsibleInProgress}
          addResponsibleIsSuccess={addResponsibleIsSuccess}
          employeesList={employeesList}
          organizationId={newApplicationInfo?.organizationId}
          title={
            newApplicationInfo
              ? `${newApplicationInfo.firstName} ${newApplicationInfo.lastName}`
              : 'New Application'
          }
          applicationChat={applicationChat ? applicationChat[0] : undefined}
          applicationScheme={newApplicationInfo?.applicationScheme}
          setError={setError}
          clearErrors={clearErrors}
          questionnarieSetError={questionnarieSetError}
          questionnarieClearErrors={questionnarieClearErrors}
          changeLogs={changeLogs}
          changeLogsPagination={changeLogsPagination}
          client={newApplicationInfo?.user}
          onSaveCrsAndFatca={onSaveCRSAndFatca}
          crsAndFatcaValues={crsAndFatcaValues
            ?.filter((item) => item.residenceCountryId)
            ?.map((item) => ({
              residenceCountryId: item?.residenceCountryId || '',
              taxIdentificationNumber: item?.taxIdentificationNumber || '',
              noTinReason: item?.noTinReason,
            }))}
          crsAndFatca={crsAndFatca?.map((item) => ({
            residenceCountryId: item?.residenceCountryId,
            taxIdentificationNumber: item?.taxIdentificationNumber || '',
            noTinReason: item?.noTinReason || '',
          }))}
          stepOneBlockLoadingInProgress={
            createAffilatedCompanyInProgress || updateAffilatedCompanyInProgress
          }
          onSaveAffilatedCompany={onSaveAffilatedCompanies}
          setHaveSecondPassport={setHaveSecondPassport}
          haveSecondPassport={haveSecondPassport}
          affilatedCompaniesValues={affilatedCompaniesValues
            ?.filter(
              (item) =>
                item.name &&
                item.registrationNumber &&
                item.mainActivities &&
                item.position &&
                item.countryId &&
                item.percentageOfOwnership,
            )
            ?.map((item) => ({
              name: item.name,
              registrationNumber: item.registrationNumber,
              mainActivities: item.mainActivities,
              position: item.position,
              countryId: item.countryId,
              percentageOfOwnership: Number(item.percentageOfOwnership),
            }))}
          affilatedCompanies={affilatedCompanies?.map((item) => ({
            name: item.name,
            registrationNumber: item.registrationNumber,
            mainActivities: item.mainActivities,
            position: item.position,
            countryId: item.countryId,
            percentageOfOwnership: Number(item.percentageOfOwnership),
          }))}
        />
        <StepItem
          title="Step 1"
          status={applicationStepsStatus?.stepOne as NewApplicationStepStatus}
          id="step-1"
        >
          <StepOne
            control={control}
            questionnarieControl={questionnarieControl}
            errors={errors}
            questionnarieErrors={questionnarieErrors}
            status={newApplicationInfo?.status}
            isEditable={
              activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
              ) && newApplicationInfo?.basePersonalInfoEditable
            }
            activePermissions={activePermissions}
            hasSumSubId={Boolean(
              newApplicationInfo?.sumSubApplication?.applicantId,
            )}
            applicationScheme={newApplicationInfo?.applicationScheme}
            clearErrors={clearErrors}
            questionnarieClearErrors={questionnarieClearErrors}
            questionnarieSetValue={questionnarieSetValue}
            groupStructurChartFile={documents?.find(
              (item) =>
                item.documentCategoryId ===
                '72b810fc-d635-4937-8a14-f9b77eaa699f',
            )}
            cofRegulatedCompanyScheme={
              newApplicationInfo?.applicationScheme?.id ===
              'a87db4b4-3c78-4564-bba1-c16a19553159'
            }
            clientActivity={fields.clientActivityId}
            questionnarieFields={questionnarieFields}
            sourceOfWealth={sourceOfWealth}
            baseCurrency={baseCurrency}
            sourceOfIncome={sourceOfIncome}
            sourceOfWealthForIndividual={sourceOfWealthForIndividual}
            fields={fields}
            affilatedCompaniesValues={affilatedCompaniesValues}
            setAffilatedCompaniesValues={setAffilatedCompaniesValues}
            affilatedCompaniesIsLoading={affilatedCompaniesIsLoading}
            affilatedCompaniesIsFetching={affilatedCompaniesIsFetching}
            deleteAffilatedCompanyInProgress={deleteAffilatedCompanyInProgress}
            onDeleteAffilatedCompany={onDeleteAffilatedCompany}
            selectedAffilatedCompanyId={selectedAffilatedCompanyId}
            uboPercentage={uboPercentage as string}
            companyOfficial
          />
        </StepItem>
        {(!newApplicationInfo?.applicationScheme ||
          (newApplicationInfo.applicationScheme &&
            newApplicationInfo.applicationScheme?.kycProvider !== 'NONE')) && (
          <StepItem
            title="Step 2"
            status={applicationStepsStatus?.stepTwo as NewApplicationStepStatus}
            id="step-2"
          >
            <StepThree
              sumSubStatus={newApplicationInfo?.sumSubApplication?.reviewAnswer}
              sumSubReviewStatus={
                newApplicationInfo?.sumSubApplication?.reviewStatus
              }
              changeLogsAvailable={activePermissions?.includes(
                PermissionEnum.AUDIT_LOG_LIST,
              )}
              sumSubId={newApplicationInfo?.sumSubApplication?.id}
              id={newApplicationInfo?.sumSubApplication?.applicantId as string}
              countries={countries}
              control={control}
              documents={documents}
              documentsVisible={activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_LIST,
              )}

              isEditable={activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
              )}
              selectedDocumentsCounts={selectedDocumentsCounts}
              status={newApplicationInfo?.status}
              applicationId={applicationId}
              onGroupSelect={(data) => dispatch(onGroupSelect(data))}
              setSelected={({ id, selected }) =>
                dispatch(setSelected({ id, selected }))
              }
              sumSubDocuments={newApplicationInfo?.idDocs?.edges?.map(
                (item) => item.node,
              )}
              search={search}
              errors={errors}
              renameAvailable={activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
              )}
              questionnarieControl={questionnarieControl}
              questionnarieClearErrors={questionnarieClearErrors}
              questionnarieErrors={questionnarieErrors}
              questionnarieFields={questionnarieFields}
              questionnarieSetValue={questionnarieSetValue}
              crsAndFatcaIsLoading={crsAndFatcaIsLoading}
              crsAndFatcaIsFetching={crsAndFatcaIsFetching}
              deleteCrsAndFatcaInProgress={deleteCrsAndFatcaInProgress}
              onDeleteCRSAndFatca={onDeleteCRSAndFatca}
              crsAndFatcaValues={crsAndFatcaValues}
              setCrsAndFatcaValues={setCrsAndFatcaValues}
              clearErrors={clearErrors}
              setHaveSecondPassport={setHaveSecondPassport}
              haveSecondPassport={haveSecondPassport}
              cofRegulatedCompanyScheme={
                newApplicationInfo?.applicationScheme?.id ===
                'a87db4b4-3c78-4564-bba1-c16a19553159'
              }
              selectedCrsAndFatcaId={selectedCrsAndFatcaId}
              allowedDocumentsCategories={allowedDocumentsCategories}
              cofWithDocuments={Boolean(
                newApplicationInfo?.applicationScheme?.id ===
                'ad811a02-87ed-4e19-ad45-770c0fd8fe41',
              )}
              activePermissions={activePermissions}
              setValue={setValue}
              fields={fields}
              cof
            />
          </StepItem>
        )}
        <StepItem
          title={
            !newApplicationInfo?.applicationScheme ||
            (newApplicationInfo.applicationScheme &&
              newApplicationInfo.applicationScheme?.kycProvider !== 'NONE')
              ? 'Step 3'
              : 'Step 2'
          }
          status={applicationStepsStatus?.stepFour as NewApplicationStepStatus}
          id="step-3"
        >
          <OngoingMonitoring
            status={newApplicationInfo?.status}
            currentEmployee={currentEmployee}
            activePermissions={activePermissions}
            isLoading={
              ongoingIsLoading || ongoingIsFetching || ongoingStatusIsLoading
            }
            ongoingMonitoring={ongoingMonitoring}
            isAllowed={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_AML_LIST,
            )}
            applyError={applyError}
            applyWarningInProgress={applyWarningInProgress}
            applyOngoingWarning={applyApplicationOngoingWarningAction}
            batchApplyWarningInProgress={batchApplyWarningInProgress}
            batchApplyOngoingWarning={batchApplyApplicationOngoingWarningAction}
            batchApplyWarningIsSuccess={batchApplyWarningIsSuccess}
            restartOngoingCheckingAction={restartOngoingCheckingAction}
            restartInProgress={restartInProgress}
            ongoingCheckingStatus={ongoingCheckingStatus}
            onResetTransactionWarningAction={onResetTransactionWarningAction}
            resetTransactionsWarningIsLoading={
              resetTransactionsWarningIsLoading
            }
            resetTransactionsWarningIsSuccess={
              resetTransactionsWarningIsSuccess
            }
            selectedWarningId={selectedWarningId}
            getApplicationWarningsAction={getApplicationWarningsAction}
            paginationInProgress={paginationInProgress || paginationIsFetching}
            selectedWarningGroup={selectedWarningGroup}
            blacklistData={blacklistData}
            setBlacklistData={setBlacklistData}
            blacklistDataVisible={blacklistDataVisible}
            setBlacklistDataVisible={setBlacklistDataVisible}
            blacklistItem={blacklistItem}
            setBlacklistDataItem={setBlacklistDataItem}
            blacklistItemVisible={blacklistItemVisible}
            setBlacklistItemVisible={setBlacklistItemVisible}
            getOngoingWarningById={getApplicationOngoingWarningByIdAction}
            isWarningByIdLoading={isWarningByIdLoading}
            isWarningByIdFetching={isWarningByIdFetching}
          />
        </StepItem>
        {(!newApplicationInfo?.applicationScheme ||
          (newApplicationInfo.applicationScheme &&
            newApplicationInfo.applicationScheme.agreementsActive) ||
          (newApplicationInfo.applicationScheme &&
            newApplicationInfo.applicationScheme.agreementsActive ===
              null)) && (
          <StepItem
            title={
              !newApplicationInfo?.applicationScheme ||
              (newApplicationInfo.applicationScheme &&
                newApplicationInfo.applicationScheme?.kycProvider !== 'NONE')
                ? 'Step 4'
                : 'Step 3'
            }
            status={
              applicationStepsStatus?.stepThree as NewApplicationStepStatus
            }
            id="step-4"
          >
            <StepFour list={agreements} errors={errors} />
          </StepItem>
        )}
      </div>
    );
  }

  return (
    <div className={cn.newApplicationInfo}>
      <div className={cn.newApplicationLinkWrapper}>
        <button
          type="button"
          className={cn.newApplicationBackLink}
          onClick={() => {
            if (isDirty || questionnarieIsDirty) {
              setDirtyPopupVisible(true);
            } else {
              navigate(
                state && state.applicationId
                  ? generatePath(
                      ROUTES.CUSTOMERS_NEW_APPLICATION_CORPORATE_INFO,
                      {
                        applicationId: state.applicationId,
                      },
                    )
                  : `${ROUTES.CUSTOMERS_NEW_APPLICATION_LIST}${search || ''}`,
              );
              dispatch(clearNewApplicationInfoState());
            }
          }}
        >
          <IconArrowLeft width={13} height={11} />
          Back
        </button>
      </div>
      {content}
      <Outlet />
      {hintVisible && (
        <div className={cn.customerCreatedSuccessful}>
          <div className={cn.customerCreatedSuccessfulLeft}>
            <span>
              <IconSelected />
            </span>
            <span>Customer created</span>
          </div>
          <div className={cn.customerCreatedSuccessfulRight}>
            <span>
              <NavLink
                to={generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_PAGE, {
                  customerId: (newApplicationInfo?.userId as string) || '',
                })}
              >
                <IconArrowRight />
                Open application
              </NavLink>
            </span>
            <button
              type="button"
              className={cn.hintCloseButton}
              onClick={() => dispatch(setHintVisible(false))}
            >
              Close
            </button>
          </div>
        </div>
      )}
      <Popup
        title="Reject application?"
        description="Application will be closed"
        onCancelClick={() => setPopupVisible(false)}
        onOkClick={() => {
          onRejectAplication();
        }}
        cancelButtonText="Cancel"
        okButtonText={rejectInProgress ? 'Loading...' : 'Reject'}
        isVisible={popupVisible}
      />
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={() =>
          navigate(
            state && state.applicationId
              ? generatePath(ROUTES.CUSTOMERS_NEW_APPLICATION_CORPORATE_INFO, {
                  applicationId: state.applicationId,
                })
              : `${ROUTES.CUSTOMERS_NEW_APPLICATION_LIST}${search || ''}`,
          )
        }
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
      />
      <div id="signature-popup" />
    </div>
  );
};

export default NewApplicationMainRepresentativeInfo;
